/**
 * Browser-generated `X-Request-Id`, off by default.
 *
 * The point is joining: shared-lib's `RequestIdFilter` accepts an inbound
 * `X-Request-Id` matching `^[A-Za-z0-9._-]{8,64}$`, puts it in log4j2's
 * ThreadContext (so every GELF line carries `requestId`) and propagates it on
 * outbound edge<->api calls. With the browser supplying it, one console error
 * or one API_ERROR event points straight at the api AND edge log lines that
 * produced it — which on 2026-09-01 was the single thing nobody could do.
 *
 * Nothing here runs unless `featureFlags.requestIdHeader` is on; see
 * `MetricsService` for the gate and why it is off by default.
 */

/** Exactly the regex shared-lib's RequestIdFilter validates against. */
export const REQUEST_ID_PATTERN = /^[A-Za-z0-9._-]{8,64}$/;

/**
 * Longest session prefix we keep. The suffix is `-<counter>`; capping the
 * prefix at 48 leaves 16 characters of headroom, which is more counter than any
 * page load will ever use, and keeps the whole value inside the 64-char limit
 * the backend enforces.
 */
const MAX_PREFIX_LENGTH = 48;
const MIN_PREFIX_LENGTH = 8;

let enabled = false;
let sessionPrefix = '';
/** Per page load: the module is re-evaluated on every full navigation. */
let counter = 0;

/**
 * Reduce a session id to the character set the backend accepts, and guarantee
 * it is long enough to satisfy the minimum on its own. A session id that is
 * short or oddly shaped (SocketJS ids come from `Math.random().toString(36)`,
 * which can be shorter than it looks) is padded rather than rejected — a
 * request with no id at all is worse than one with a synthetic prefix.
 */
export function sanitizeSessionPrefix(sessionId: string): string {
    const cleaned = (sessionId || '').replace(/[^A-Za-z0-9._-]/g, '').slice(0, MAX_PREFIX_LENGTH);
    if (cleaned.length >= MIN_PREFIX_LENGTH) return cleaned;
    const pad = Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
    return (cleaned + pad).slice(0, MAX_PREFIX_LENGTH);
}

/**
 * Turn the header on/off and set the session prefix. Called from the feature
 * flag subscription; safe to call repeatedly (the counter is not reset, so ids
 * stay unique across a flag flip mid-session).
 */
export function configureRequestIdHeader(options: { enabled: boolean, sessionId?: string }): void {
    enabled = !!options.enabled;
    if (options.sessionId != null) {
        sessionPrefix = sanitizeSessionPrefix(options.sessionId);
    }
}

/** True when the header should be attached. */
export function isRequestIdHeaderEnabled(): boolean {
    return enabled;
}

/**
 * The next id, or null when the flag is off or the value would not satisfy the
 * backend's regex (in which case sending it is worse than sending nothing —
 * the filter would discard it and generate its own, silently).
 */
export function nextRequestId(): string | null {
    if (!enabled) return null;
    if (!sessionPrefix) {
        sessionPrefix = sanitizeSessionPrefix('');
    }
    counter += 1;
    const id = `${sessionPrefix}-${counter}`;
    return REQUEST_ID_PATTERN.test(id) ? id : null;
}

/** Header object to merge into a request, or null. */
export function requestIdHeader(): { 'X-Request-Id': string } | null {
    const id = nextRequestId();
    return id ? { 'X-Request-Id': id } : null;
}

/** Test seam. */
export function resetRequestIdForTest(): void {
    enabled = false;
    sessionPrefix = '';
    counter = 0;
}
